import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import type { AgentPresetUpsertInput } from '@cp3/shared';
import { functions } from '../lib/firebase';
import { useUser } from '../auth/AuthProvider';

export interface AgentPresetRow extends AgentPresetUpsertInput {
  createdAt?: unknown;
  updatedAt?: unknown;
}

const QK = ['agent-presets'] as const;

export function useAgentPresets() {
  const { user } = useUser();
  return useQuery<AgentPresetRow[]>({
    queryKey: QK,
    enabled: !!user,
    staleTime: 30_000,
    queryFn: async () => {
      const fn = httpsCallable<unknown, { items: AgentPresetRow[] }>(
        functions,
        'listAgentPresets',
      );
      const r = await fn({});
      return r.data.items;
    },
  });
}

export function useUpsertAgentPreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AgentPresetUpsertInput) => {
      const fn = httpsCallable<AgentPresetUpsertInput, { ok: true; id: string }>(
        functions,
        'upsertAgentPreset',
      );
      const r = await fn(input);
      return r.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK });
    },
  });
}

export function useDeleteAgentPreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (presetId: string) => {
      const fn = httpsCallable<{ presetId: string }, { ok: true }>(
        functions,
        'deleteAgentPreset',
      );
      const r = await fn({ presetId });
      return r.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK });
    },
  });
}

export function useReorderAgentPresets() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (entries: Array<{ id: string; sortOrder: number }>) => {
      const fn = httpsCallable<{ entries: typeof entries }, { ok: true; count: number }>(
        functions,
        'reorderAgentPresets',
      );
      const r = await fn({ entries });
      return r.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK });
    },
  });
}

export function useSeedAgentPresets() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (overwrite: boolean) => {
      const fn = httpsCallable<
        { overwrite: boolean },
        { ok: true; written: string[]; skipped: string[] }
      >(functions, 'seedAgentPresets');
      const r = await fn({ overwrite });
      return r.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK });
    },
  });
}
